import i18next from 'i18next';
import { AnyAction } from 'redux';
import { SagaIterator } from 'redux-saga';
import { call, put, takeLatest } from 'redux-saga/effects';
import {
    editVideoAdmin,
    fetchVideoCategoriesAdmin,
    fetchVideoCategoriesByTopicIdAdmin,
    fetchVideoRatingsAdmin,
    fetchVideosAdmin,
    fetchVideosByVideoCategoryIdAdmin,
    fetchVideoTopicsAdmin,
    submitInsertVideoAdmin,
} from 'src/api/video-admin';
import { enqueueSnackbarAction } from '../notifications/actions';
import {
    requestCreateLanguageBlock,
    requestCreateLanguageBlockFailure,
    requestCreateLanguageBlockSuccess,
    requestEditLanguageBlockTranslationSuccess,
} from '../templateAdmin/actions';
import {
    CREATE_NEW_VIDEO_ADMIN,
    EDIT_VIDEO_ADMIN,
    FETCH_VIDEOS_BY_CATEGORY_ADMIN,
    FETCH_VIDEOS_WITHOUT_CATEGORY_ADMIN,
    FETCH_VIDEO_CATEGORIES_ADMIN,
    FETCH_VIDEO_CATEGORIES_BY_TOPICID_ADMIN,
    FETCH_VIDEO_RATINGS_ADMIN,
    FETCH_VIDEO_TOPICS_ADMIN,
    requestEditVideoAdmin,
    requestEditVideoAdminFailure,
    requestVideoCategoriesAdmin,
    requestVideoCategoriesAdminFailure,
    requestVideoCategoriesAdminSuccess,
    requestVideoCategoriesByTopicIdAdmin,
    requestVideoCategoriesByTopicIdAdminFailure,
    requestVideoCategoriesByTopicIdAdminSuccess,
    requestVideoRatingsAdmin,
    requestVideoRatingsAdminFailure,
    requestVideoRatingsAdminSuccess,
    requestVideosByCategoryAdmin,
    requestVideosByCategoryAdminFailure,
    requestVideosByCategoryAdminSuccess,
    requestVideosWithoutCategoryAdmin,
    requestVideosWithoutCategoryAdminFailure,
    requestVideosWithoutCategoryAdminSuccess,
    requestVideoTopicsAdmin,
    requestVideoTopicsAdminFailure,
    requestVideoTopicsAdminSuccess,
} from './actions';

/* Video Topics */
function* fetchVideoTopicsAdminSaga(action: AnyAction): SagaIterator {
    yield put(requestVideoTopicsAdmin());

    const { error, response } = yield call(fetchVideoTopicsAdmin);

    if (error) {
        yield put(
            enqueueSnackbarAction({
                message: i18next.t('it:pages.videos.video_topics.loadingFailure'),
                options: {
                    key: new Date().getTime() + Math.random(),
                    variant: 'error',
                },
            })
        );
        yield put(requestVideoTopicsAdminFailure(error));
    } else {
        yield put(
            enqueueSnackbarAction({
                message: i18next.t('it:pages.videos.video_topics.loadingSuccess'),
                options: {
                    key: new Date().getTime() + Math.random(),
                    variant: 'success',
                },
            })
        );
        yield put(requestVideoTopicsAdminSuccess(response));
    }
}

export function* generateFetchVideoTopicsAdminWatcher() {
    yield takeLatest(FETCH_VIDEO_TOPICS_ADMIN, fetchVideoTopicsAdminSaga);
}

/* Video Categories by topicId */
function* fetchVideoCategoriesByTopicIdAdminSaga(action: AnyAction): SagaIterator {
    yield put(requestVideoCategoriesByTopicIdAdmin());

    const { error, response } = yield call(fetchVideoCategoriesByTopicIdAdmin, action.topicId);

    if (error) yield put(requestVideoCategoriesByTopicIdAdminFailure(error));
    else yield put(requestVideoCategoriesByTopicIdAdminSuccess(response));
}

export function* generateFetchVideoCategoriesByTopicIdAdminWatcher() {
    yield takeLatest(FETCH_VIDEO_CATEGORIES_BY_TOPICID_ADMIN, fetchVideoCategoriesByTopicIdAdminSaga);
}

/* All Video Categories */
function* fetchAllVideoCategoriesAdminSaga(action: AnyAction): SagaIterator {
    yield put(requestVideoCategoriesAdmin());

    const { error, response } = yield call(fetchVideoCategoriesAdmin);

    if (error) yield put(requestVideoCategoriesAdminFailure(error));
    else yield put(requestVideoCategoriesAdminSuccess(response));
}

export function* generateFetchAllVideoCategoriesAdminWatcher() {
    yield takeLatest(FETCH_VIDEO_CATEGORIES_ADMIN, fetchAllVideoCategoriesAdminSaga);
}

/* Videos By Category */
function* fetchVideosByCategoryAdminSaga(action: AnyAction): SagaIterator {
    yield put(requestVideosByCategoryAdmin());

    const { error, response } = yield call(fetchVideosByVideoCategoryIdAdmin, action.videoCategoryId);

    if (error) yield put(requestVideosByCategoryAdminFailure(error));
    else yield put(requestVideosByCategoryAdminSuccess(response));
}

export function* generateFetchVideosByCategoryAdminWatcher() {
    yield takeLatest(FETCH_VIDEOS_BY_CATEGORY_ADMIN, fetchVideosByCategoryAdminSaga);
}

/* Videos Without Category */
function* fetchVideosWithoutCategoryAdminSaga(action: AnyAction): SagaIterator {
    yield put(requestVideosWithoutCategoryAdmin());

    const { error, response } = yield call(fetchVideosAdmin);

    if (error) yield put(requestVideosWithoutCategoryAdminFailure(error));
    else yield put(requestVideosWithoutCategoryAdminSuccess(response));
}

export function* generateFetchVideosWithoutCategoryAdminWatcher() {
    yield takeLatest(FETCH_VIDEOS_WITHOUT_CATEGORY_ADMIN, fetchVideosWithoutCategoryAdminSaga);
}

/* Create new Video */
function* createNewVideoSaga(action: AnyAction): SagaIterator {
    yield put(requestCreateLanguageBlock());

    const { error, response } = yield call(submitInsertVideoAdmin, action.newVideoRequest);

    if (error) {
        yield put(
            enqueueSnackbarAction({
                message: i18next.t('it:pages.videos.createVideo.createVideoFailure') + '\n' + error.message,
                options: {
                    key: new Date().getTime() + Math.random(),
                    variant: 'error',
                },
            })
        );
        yield put(requestCreateLanguageBlockFailure(error));
    } else {
        yield put(
            enqueueSnackbarAction({
                message: i18next.t('it:pages.videos.createVideo.createVideoSuccess'),
                options: {
                    key: new Date().getTime() + Math.random(),
                    variant: 'success',
                },
            })
        );
        yield put(requestCreateLanguageBlockSuccess(response));
    }
}

export function* generateCreateNewVideoAdminWatcher() {
    yield takeLatest(CREATE_NEW_VIDEO_ADMIN, createNewVideoSaga);
}

/* Edit Video */
function* editVideoSaga(action: AnyAction): SagaIterator {
    yield put(requestEditVideoAdmin());

    const { error, response } = yield call(editVideoAdmin, action.newVideoRequest);

    if (error) {
        yield put(
            enqueueSnackbarAction({
                message: i18next.t('it:pages.videos.editVideo.editVideoFailure') + '\n' + error.message,
                options: {
                    key: new Date().getTime() + Math.random(),
                    variant: 'error',
                },
            })
        );
        yield put(requestEditVideoAdminFailure(error));
    } else {
        yield put(
            enqueueSnackbarAction({
                message: i18next.t('it:pages.videos.editVideo.editVideoSuccess'),
                options: {
                    key: new Date().getTime() + Math.random(),
                    variant: 'success',
                },
            })
        );
        yield put(requestEditLanguageBlockTranslationSuccess(response));
    }
}

export function* generateEditVideoAdminWatcher() {
    yield takeLatest(EDIT_VIDEO_ADMIN, editVideoSaga);
}

/* Video Ratings */
function* fetchVideoRatingsAdminSaga(action: AnyAction): SagaIterator {
    yield put(requestVideoRatingsAdmin());

    const { error, response } = yield call(fetchVideoRatingsAdmin, action.videoId, action.filter, action.pageable);

    if (error) yield put(requestVideoRatingsAdminFailure(error));
    else yield put(requestVideoRatingsAdminSuccess(response));
}

export function* generateFetchVideoRatingsAdminWatcher() {
    yield takeLatest(FETCH_VIDEO_RATINGS_ADMIN, fetchVideoRatingsAdminSaga);
}
